import { Alert, Box, Divider, Stack, Typography } from '@mui/material';
import {
  CalendarOutlined,
  CloudServerOutlined,
  CreditCardOutlined,
  ExclamationCircleOutlined,
  SaveOutlined,
  StopOutlined,
  TagOutlined,
  TeamOutlined,
  WalletOutlined
} from '@ant-design/icons';
import {
  DEDICATED_ENVS_RELEASED_TITLE,
  dedicatedEnvsReleasedNotice,
  ROOMS_AFFECTED_TITLE,
  roomsAffectedNotice
} from 'features/subscription-freeze/content';
import type { OpenTradeAccount } from 'features/subscription-freeze/openTrades';
import { useMyRooms } from 'sections/rooms/useMyRooms';
import { Consequence } from '../billing';
import { CONSEQUENCES, PAUSE_REVIEW_STEP } from '../content';
import PauseOpenTrades from './PauseOpenTrades';
import { AcknowledgeCheck, Callout, CONSEQUENCE_ICONS, Group, GroupList, IconRow } from './StepParts';

type Props = {
  restartsOn: string;
  /** What the restart charges at today's prices, when billing-manager could price it. */
  restartCharge?: string;
  /** A cancel offer they took still waits for their next payment; pausing gives it up. */
  offerWaiting?: boolean;
  /** What switches off while paused: the same list a cancel shows, for the same plans. */
  consequences: Consequence[];
  dedicatedEnvs: number;
  rooms: number;
  openTrades: { loading: boolean; failed: boolean; accounts: OpenTradeAccount[]; unchecked: OpenTradeAccount[] };
  acknowledged: boolean;
  onAcknowledgedChange: (acknowledged: boolean) => void;
  error: string;
  disabled: boolean;
};

/** Everything a pause does to their access and their money, before it happens. */
export default function PauseReviewStep({
  restartsOn,
  restartCharge,
  offerWaiting,
  consequences,
  dedicatedEnvs,
  rooms,
  openTrades,
  acknowledged,
  onAcknowledgedChange,
  error,
  disabled
}: Props) {
  // Rooms come from the plans being paused; their members only from the rooms service, once it answers.
  const { rooms: ownedRooms } = useMyRooms();
  const members = ownedRooms.filter((room) => room.active).reduce((total, room) => total + room.members, 0);

  return (
    <Stack spacing={2.5}>
      {error && <Alert severity="error">{error}</Alert>}

      <Callout icon={<CalendarOutlined />} tone="primary">
        <Typography variant="body2">
          {PAUSE_REVIEW_STEP.pausedUntil}{' '}
          <Box component="strong" sx={{ fontWeight: 600 }}>
            {restartsOn}
          </Box>
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {PAUSE_REVIEW_STEP.restartNote}
        </Typography>
      </Callout>

      {rooms > 0 && (
        <Callout icon={<TeamOutlined />} tone="warning" title={ROOMS_AFFECTED_TITLE}>
          <Typography variant="body2" color="text.secondary">
            {roomsAffectedNotice(rooms, members, restartsOn)}
          </Typography>
        </Callout>
      )}
      {offerWaiting && (
        <Callout icon={<TagOutlined />} tone="warning" title={PAUSE_REVIEW_STEP.offerTitle}>
          <Typography variant="body2" color="text.secondary">
            {PAUSE_REVIEW_STEP.offerBody}
          </Typography>
        </Callout>
      )}
      {dedicatedEnvs > 0 && (
        <Callout icon={<CloudServerOutlined />} tone="warning" title={DEDICATED_ENVS_RELEASED_TITLE}>
          <Typography variant="body2" color="text.secondary">
            {dedicatedEnvsReleasedNotice(dedicatedEnvs)}
          </Typography>
        </Callout>
      )}

      <GroupList>
        {consequences.length > 0 && (
          <>
            <Group label={PAUSE_REVIEW_STEP.pausedGroup}>
              {consequences.map((consequence) => (
                <IconRow key={consequence} icon={CONSEQUENCE_ICONS[consequence]} tone="error" label={CONSEQUENCES[consequence]} />
              ))}
            </Group>
            <Divider />
          </>
        )}
        <Group label={PAUSE_REVIEW_STEP.keptGroup}>
          <IconRow icon={<SaveOutlined />} tone="success" label={PAUSE_REVIEW_STEP.kept.setup} />
          <IconRow icon={<WalletOutlined />} tone="success" label={PAUSE_REVIEW_STEP.kept.credit} />
        </Group>
        <Divider />
        <Group label={PAUSE_REVIEW_STEP.restartGroup(restartsOn)}>
          <IconRow icon={<CreditCardOutlined />} tone="primary" label={PAUSE_REVIEW_STEP.restart.charge(restartCharge)} />
          <IconRow icon={<ExclamationCircleOutlined />} tone="primary" label={PAUSE_REVIEW_STEP.restart.declined} />
        </Group>
        <Divider />
        <Group label={PAUSE_REVIEW_STEP.leaveGroup}>
          <IconRow icon={<StopOutlined />} tone="warning" label={PAUSE_REVIEW_STEP.leave} />
        </Group>
      </GroupList>

      {/* Right above the consent, so it is the last thing read before ticking. */}
      <PauseOpenTrades
        loading={openTrades.loading}
        failed={openTrades.failed}
        accounts={openTrades.accounts}
        unchecked={openTrades.unchecked}
      />

      <AcknowledgeCheck
        tone="primary"
        label={PAUSE_REVIEW_STEP.acknowledge(restartsOn)}
        checked={acknowledged}
        onChange={onAcknowledgedChange}
        disabled={disabled}
      />
    </Stack>
  );
}
